/**
 * One answer to "is this token's impersonation scope still live?", shared by
 * every client that has to act on it.
 *
 * It needs to be shared because the two consumers reached OPPOSITE conclusions
 * from the same token and produced a state with no way out: `apiClient` treated
 * an expired `impersonator` claim as dead (correctly — it stops asking the API
 * to prefer a Bearer the server is guaranteed to reject), while `AuthGuard`
 * treated ANY `impersonator` claim as a live session and reported the app
 * AUTHENTICATED. A reload on a lapsed support session therefore rendered the
 * whole management app against a credential every protected request refused.
 *
 * The subtlety that makes this worth a named helper: `impersonator.exp` is the
 * lifetime of the SCOPE, and it is not the lifetime of the token carrying it.
 * Firebase refreshes an ID token minted from a custom token indefinitely and
 * preserves the developer claims verbatim, so a browser that once held a
 * support session keeps presenting the same expired `exp` forever — until the
 * user signs out of that origin. "Has an impersonator claim" and "is currently
 * impersonating" are different questions, and only the second one is useful.
 */

/** The impersonation block as it appears on a verified ID token. */
export interface ImpersonatorClaim {
  uid?: string;
  sessionId?: string;
  /** Unix SECONDS at which the scope lapses. */
  exp?: unknown;
  kind?: 'user' | 'support-scope' | string;
}

/** Narrow an arbitrary claims bag to its impersonator block, if any. */
export function readImpersonatorClaim(
  claims: Record<string, unknown> | undefined | null,
): ImpersonatorClaim | null {
  const impersonator = claims?.impersonator;
  if (!impersonator || typeof impersonator !== 'object') return null;
  return impersonator as ImpersonatorClaim;
}

/**
 * True when the token carries an impersonation scope that has NOT lapsed.
 *
 * A claim with no numeric `exp` counts as live: only the server may widen or
 * narrow the window, and a claim shape we do not recognise must not be silently
 * treated as expired.
 */
export function hasLiveImpersonatorClaim(
  token: { claims?: Record<string, unknown> } | null | undefined,
): boolean {
  const impersonator = readImpersonatorClaim(token?.claims);
  if (!impersonator) return false;
  const { exp } = impersonator;
  if (typeof exp !== 'number') return true;
  return exp > Math.floor(Date.now() / 1000);
}

/**
 * True when the token carries an impersonation scope that HAS lapsed.
 *
 * Distinct from `!hasLiveImpersonatorClaim(...)`, which is also true for an
 * ordinary session. Callers that must treat a dead scope differently from a
 * normal one (sign the session out rather than let it render) need this one.
 */
export function hasExpiredImpersonatorClaim(
  token: { claims?: Record<string, unknown> } | null | undefined,
): boolean {
  return (
    readImpersonatorClaim(token?.claims) !== null &&
    !hasLiveImpersonatorClaim(token)
  );
}
